import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'

export const usePrestationStore = defineStore('prestation', () => {
  const prestations = ref([])

  // Modal states
  const beneficiaireModal = ref({
    show: false,
    prestationId: null,
    currentBeneficiaire: null
  })

  const dateModal = ref({
    show: false,
    prestationId: null,
    currentDate: null,
    currentCreneau: null
  })

  const toursModal = ref({
    show: false,
    prestationId: null,
    currentTours: 0,
    selectedTours: 0,
    availableTours: [],
    tourData: null
  })

  // Loading states
  const loading = ref({
    prestations: false,
    beneficiaires: false,
    dates: false,
    updateBeneficiaire: false,
    updateDate: false,
    tours: false,
    updateTours: false
  })

  // Data states
  const availableBeneficiaires = ref([])
  const availableDates = ref([])
  const error = ref(null)

  // Beneficiaire Modal Actions
  function openBeneficiaireModal(prestationId, currentBeneficiaire) {
    beneficiaireModal.value = {
      show: true,
      prestationId,
      currentBeneficiaire
    }
    
    if (availableBeneficiaires.value.length === 0) {
      fetchAvailableBeneficiaires()
    }
  }

  function closeBeneficiaireModal() {
    beneficiaireModal.value = {
      show: false,
      prestationId: null,
      currentBeneficiaire: null
    }
    error.value = null
  }

  // Date Modal Actions
  function openDateModal(prestationId, currentDate, currentCreneau = null) {
    dateModal.value = {
      show: true,
      prestationId,
      currentDate,
      currentCreneau
    }
    
    // Charger les dates disponibles si pas déjà chargées
    const prestation = prestations.value.find(p => p.id === prestationId)
    if (prestation && Object.keys(availableDates.value).length === 0) {
      fetchAvailableDates(prestation.circuit.id, prestation.stage.id, prestation.voiture.id)
    }
  }

  function closeDateModal() {
    dateModal.value = {
      show: false,
      prestationId: null,
      currentDate: null,
      currentCreneau: null
    }
    error.value = null
    // Nettoyer les dates disponibles pour éviter les confusions
    availableDates.value = {}
  }

  // Tours Modal Actions
  function openToursModal(prestationId, currentTours) {
    toursModal.value = {
      show: true,
      prestationId,
      currentTours,
      selectedTours: currentTours + 1, // Commencer avec un tour supplémentaire minimum
      availableTours: [],
      tourData: null
    }
    
    fetchAvailableTours(prestationId)
  }

  function closeToursModal() {
    toursModal.value = {
      show: false,
      prestationId: null,
      currentTours: 0,
      selectedTours: 0,
      availableTours: [],
      tourData: null
    }
    error.value = null
  }

  async function fetchPrestations() {
    loading.value.prestations = true
    error.value = null
    try {
      const response = await axios.get('/api/prestations')
      prestations.value = response.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors du chargement des prestations'
      console.error('Error fetching prestations:', err)
    } finally {
      loading.value.prestations = false
    }
  }

  // API Actions (à implémenter plus tard)
  async function fetchAvailableBeneficiaires() {
    loading.value.beneficiaires = true
    error.value = null
    try {
      const response = await axios.get('/api/beneficiaires')
      availableBeneficiaires.value = response.data.data
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors du chargement des bénéficiaires'
      console.error('Error fetching beneficiaires:', err)
    } finally {
      loading.value.beneficiaires = false
    }
  }

  async function fetchAvailableDates(circuitId, stageId, voitureId = null) {
    loading.value.dates = true
    error.value = null
    try {
      const params = new URLSearchParams({
        circuit_id: circuitId,
        stage_id: stageId
      })
      
      const response = await axios.get(`/api/available-stages?${params.toString()}`)
      
      // La réponse contient des stages avec leurs dates
      // On récupère les dates depuis le premier stage
      const stages = response.data.stages || []
      if (stages.length > 0) {
        availableDates.value = stages[0].dates || {}
      } else {
        availableDates.value = {}
      }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors du chargement des dates'
      console.error('Error fetching dates:', err)
    } finally {
      loading.value.dates = false
    }
  }

  async function requestBeneficiaryChange(prestationId, beneficiaireId) {
    error.value = null
    try {
      const response = await axios.post(`/api/prestations/${prestationId}/beneficiary-change`, { beneficiaire_id: beneficiaireId })
      return response.data
    } catch (err) {
      error.value = Object.values(err.response?.data?.errors || {}).flat()[0] || err.response?.data?.message || 'Impossible de créer la demande de changement.'
      throw err
    }
  }

  async function updateBeneficiaire(prestationId, newBeneficiaireId) {
    loading.value.updateBeneficiaire = true
    error.value = null
    try {
      const response = await axios.post(`/api/prestations/${prestationId}/beneficiaire`, {
        beneficiaire_id: newBeneficiaireId
      })

      prestations.value = prestations.value.map(prestation => {
        if (prestation.id === prestationId) {
          return response.data
        }
        return prestation
      })
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de la modification du bénéficiaire'
      console.error('Error updating beneficiaire:', err)
      throw err
    } finally {
      loading.value.updateBeneficiaire = false
    }
  }

  async function updateDate(prestationId, circuitDateId, circuitDateCreneauId) {
    loading.value.updateDate = true
    error.value = null
    try {
      const response = await axios.post(`/api/prestations/${prestationId}/date`, {
        circuit_date_id: circuitDateId,
        circuit_date_creneau_id: circuitDateCreneauId
      })

      if (response.data.payment_url) {
        return { payment_url: response.data.payment_url }
      }

      // Mettre à jour la prestation dans le store
      prestations.value = prestations.value.map(prestation => {
        if (prestation.id === prestationId) {
          return response.data
        }
        return prestation
      })

      return {
        success: true,
        message: 'Date modifiée avec succès',
        data: response.data
      }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de la modification de la date'
      console.error('Error updating date:', err)
      throw err
    } finally {
      loading.value.updateDate = false
    }
  }

  async function fetchAvailableTours(prestationId) {
    loading.value.tours = true
    error.value = null
    try {
      const response = await axios.get(`/api/prestations/${prestationId}/tours-options`)
      
      if (response.data.success) {
        const data = response.data.data
        toursModal.value.availableTours = data.tours || []
        toursModal.value.tourData = {
          stage_id: data.stage_id,
          circuit_id: data.circuit_id,
          nb_tour_min: data.nb_tour_min,
          nb_tour_max: data.nb_tour_max,
          montant_ttc_base: data.montant_ttc_base,
          montant_ttc_par_tour: data.montant_ttc_par_tour
        }
      }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors du chargement des options de tours'
      console.error('Error fetching tours options:', err)
    } finally {
      loading.value.tours = false
    }
  }

  async function updateTours(prestationId, newToursCount) {
    loading.value.updateTours = true
    error.value = null
    try {
      const response = await axios.post(`/api/prestations/${prestationId}/tours`, {
        tours: newToursCount
      })

      if (response.data.success) {
        // Mettre à jour la prestation dans le store
        prestations.value = prestations.value.map(prestation => {
          if (prestation.id === prestationId) {
            return response.data.data
          }
          return prestation
        })

        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        }
      }
    } catch (err) {
      error.value = err.response?.data?.message || 'Erreur lors de la modification des tours'
      console.error('Error updating tours:', err)
      throw err
    } finally {
      loading.value.updateTours = false
    }
  }

  // Méthodes utilitaires pour accéder aux données des dates
  function getAvailableDatesArray() {
    return Object.values(availableDates.value)
  }

  function getCreneauxForDate(circuitDateId) {
    const dateData = availableDates.value[circuitDateId]
    return dateData ? dateData.creneaux || [] : []
  }

  function findCreneauById(circuitDateCreneauId) {
    for (const dateData of Object.values(availableDates.value)) {
      const creneau = dateData.creneaux?.find(c => c.circuit_date_creneau_id === circuitDateCreneauId)
      if (creneau) {
        return creneau
      }
    }
    return null
  }

  return {
    // States
    beneficiaireModal,
    dateModal,
    toursModal,
    loading,
    availableBeneficiaires,
    availableDates,
    error,
    prestations,
    // Actions
    openBeneficiaireModal,
    closeBeneficiaireModal,
    openDateModal,
    closeDateModal,
    openToursModal,
    closeToursModal,
    fetchPrestations,
    fetchAvailableBeneficiaires,
    fetchAvailableDates,
    fetchAvailableTours,
    updateBeneficiaire,
    requestBeneficiaryChange,
    updateDate,
    updateTours,
    // Utilitaires
    getAvailableDatesArray,
    getCreneauxForDate,
    findCreneauById
  }
}) 